import { IAdFoxBannerOnErrorError, IAdFoxBannerOnLoadData } from '@cian/adfox-component';
import { withAdfoxPostViewAnalytics } from '@cian/postview-analytics-utils';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';

import { AdfoxBanner } from './AdfoxBanner';
import { BlockErrorBoundary } from '../BlockErrorBoundary';
import { useAdfoxContext } from './external/context/AdfoxContext';

import type { JSX } from 'react';

type AdfoxBannerContainerProps = {
  galleryPosition?: number;
  position?: number;
  onError?(error: IAdFoxBannerOnErrorError): void;
  onLoad?(data: IAdFoxBannerOnLoadData): void;
  onStub?(): void;
};

type HandleError = (error: IAdFoxBannerOnErrorError) => void;

export const AdfoxBannerContainer = React.memo<AdfoxBannerContainerProps>(function AdfoxBannerContainer(props) {
  const { position, galleryPosition, onStub, onError, onLoad } = props;

  const { state: isBannerHidden, setTrue: setBannerHidden } = useBooleanState();
  const { params, sizes, postViewAnalyticsData } = useAdfoxContext();

  const handleStub = React.useCallback<VoidFunction>(() => {
    onStub?.();

    setBannerHidden();
  }, [onStub, setBannerHidden]);

  const handleError = React.useCallback<HandleError>(
    error => {
      onError?.(error);

      setBannerHidden();
    },
    [onError, setBannerHidden],
  );

  return React.useMemo<JSX.Element | null>(() => {
    if (isBannerHidden) {
      return null;
    }

    const Banner = withAdfoxPostViewAnalytics(AdfoxBanner, {
      ...postViewAnalyticsData,
      galleryPosition,
      position,
    });

    return (
      <BlockErrorBoundary>
        <Banner {...sizes} params={params} onStub={handleStub} onError={handleError} onLoad={onLoad} />
      </BlockErrorBoundary>
    );
  }, [isBannerHidden]);
});
